/**
 * The Ledger — one screen, four tabs, and almost nothing new computed.
 *
 * The whole argument for this file is in what it does *not* do. The settlement
 * has been computing an income breakdown every tick and showing four numbers of
 * it in a tooltip; it has been computing a morale target out of eight named
 * terms and showing the result as one word; `stepProduction` has been working
 * out which input a converter ran short of and throwing the answer away;
 * `diplomaticView` has been returning a sentence for every greyed-out button and
 * nothing has ever drawn one. Every panel below is a *read* of something that
 * was already there.
 *
 * Rules this screen keeps:
 *   • It writes nothing. Every control posts a `Command` and waits for the next
 *     frame to tell it what happened, exactly like the build menu.
 *   • Diplomacy goes to the **realm**, not to the city the player is standing
 *     in — `onRealmCommand` exists because commands are routed to whichever
 *     world is on screen, and the crown's business is not the town's.
 *   • The markup is rebuilt wholesale a few times a second and every control is
 *     found by delegation off one listener on the root, so a redraw can never
 *     leave a dead button behind or lose a click that is halfway through.
 */

import { keepScroll, setHtml } from './dom.ts';
import { toCss, C } from '../data/palette.ts';
import { FOOD_VALUE, VILLAGER_SHARE } from '../sim/constants.ts';
import {
  RESOURCE_GLYPHS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  TAX_BANDS,
  Team,
  UNCAPPED_RESOURCES,
  type ResourceKey,
} from '../sim/types.ts';
import { Cmd, cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { buildingType, BUILDING_TYPES } from '../data/buildings.ts';
import { hudResources } from '../data/ages.ts';
import {
  foodDrawPerMin,
  grainDemandPerMin,
  larderWorth,
  moraleTarget,
  moraleTerms,
  productionReadout,
  prosperity,
  taxProjection,
} from '../sim/systems/settlement.ts';
import { season, seasonUpkeep, seasonYield, formatDateLong } from '../sim/systems/calendar.ts';
import {
  diplomaticView,
  offerText,
  pendingOffers,
  warView,
  ALLIANCE_OPINION,
  DiploAction,
  MARRIAGE_OPINION,
} from '../sim/campaign/diploacts.ts';
import { CB_NAMES, SUBJECT_NAMES } from '../sim/campaign/diplotypes.ts';
import { BRANCH_NAMES, techFor, type TechBranch } from '../data/tech.ts';
import { race } from '../data/races.ts';
import { academyTier, researchError } from '../sim/apply.ts';
import { demographySnapshot, jobHands, musterAdultRefusal } from '../sim/systems/demography.ts';
import { musterRoom } from '../sim/systems/companies.ts';
import {
  ADULT_MONTHS,
  CHILDHOOD_MONTHS,
  ELDER_MONTHS,
  MUSTER_GRACE_HEADS,
} from '../data/demography.ts';
import { unitType, unitTypeIndex } from '../data/units.ts';

export type ManageTab = 'realm' | 'powers' | 'people' | 'production' | 'research';

const TABS: Array<{ id: ManageTab; label: string; sub: string }> = [
  { id: 'realm', label: 'Realm', sub: 'The purse, and the dial that fills it' },
  { id: 'powers', label: 'Powers', sub: 'Who else is out there, and what they make of you' },
  { id: 'people', label: 'People', sub: 'Beds, bread and mood' },
  { id: 'production', label: 'Production', sub: 'Every chain, and what is choking it' },
  { id: 'research', label: 'Study', sub: 'The Academy, and everything it will teach you' },
];

export interface ManageCallbacks {
  /** Move the tax dial. `policy` indexes `TAX_BANDS`. */
  onTaxPolicy(policy: number): void;
  /**
   * A command for the realm world.
   *
   * `main.ts` routes the command queue to whichever world is on screen, and
   * this screen is opened from the city — so a declaration of war pushed the
   * ordinary way would be handed to a settlement that has never heard of a
   * nation and silently dropped.
   */
  onRealmCommand(c: Command): void;
  /**
   * A command for the settlement this sheet was opened from.
   *
   * Distinct from `onRealmCommand` for the reason that one exists: `main.ts`
   * routes the queue to whichever world is on screen, and these two go to
   * different worlds. Research is bought by a town, not by a crown.
   */
  onCityCommand(c: Command): void;
}

/** How often the sheet is rebuilt. Four times a second is faster than reading. */
const REDRAW = 0.25;

export class Manage {
  private el: HTMLElement;
  private visible = false;
  private tab: ManageTab = 'realm';
  private timer = 0;
  private city: World | null = null;
  private realm: World | null = null;

  constructor(private cb: ManageCallbacks) {
    injectManageStyle();
    this.el = document.createElement('div');
    this.el.id = 'manage';
    this.el.style.display = 'none';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);

    /**
     * One listener, forever, and on `mousedown` rather than `click`.
     *
     * The sheet's innards are replaced four times a second, so a handler bound
     * to a node inside it would be thrown away with the node — hence the
     * delegation. And `click` only fires when press and release land on the
     * *same* element, so a redraw between the two swallows the press entirely;
     * acting on the press cannot be raced.
     */
    this.el.addEventListener('mousedown', (ev) => this.onClick(ev));
  }

  get isOpen(): boolean {
    return this.visible;
  }

  show(tab?: ManageTab): void {
    if (tab) this.tab = tab;
    this.visible = true;
    this.el.style.display = 'flex';
    this.timer = REDRAW;
  }

  hide(): void {
    this.visible = false;
    this.el.style.display = 'none';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  private onClick(ev: MouseEvent): void {
    const target = (ev.target as HTMLElement | null)?.closest('[data-act]') as HTMLElement | null;
    if (!target) return;
    if (target.classList.contains('off')) return;
    const act = target.dataset.act as string;
    const a = Number(target.dataset.a ?? '0');
    const b = Number(target.dataset.b ?? '0');
    const c = Number(target.dataset.c ?? '0');

    switch (act) {
      case 'close':
        this.hide();
        return;
      case 'tab':
        this.tab = target.dataset.tab as ManageTab;
        break;
      case 'tax':
        this.cb.onTaxPolicy(a);
        break;
      case 'war':
        this.cb.onRealmCommand(cmd(Cmd.CampaignDeclareWar, a, b));
        break;
      case 'diplo':
        this.cb.onRealmCommand(cmd(Cmd.CampaignDiplomacy, a, b));
        break;
      case 'answer':
        this.cb.onRealmCommand(cmd(Cmd.CampaignAnswerOffer, a, b));
        break;
      case 'peace':
        this.cb.onRealmCommand(cmd(Cmd.CampaignOfferPeace, a, b, c));
        break;
      case 'study':
        this.cb.onCityCommand(cmd(Cmd.Research, 0, 0, 0, 0, target.dataset.id ?? ''));
        break;
      default:
        break;
    }
    // Redraw on the next frame rather than now: the command has not been
    // applied yet, and a sheet redrawn this instant would show the old world
    // and read as a button that did nothing.
    this.timer = REDRAW;
  }

  update(city: World, realm: World | null, dt: number): void {
    if (!this.visible) return;
    this.city = city;
    this.realm = realm;
    this.timer += dt;
    if (this.timer < REDRAW) return;
    this.timer = 0;
    this.draw();
  }

  /**
   * The sheet, rebuilt — but only when it would say something different, and
   * never at the cost of where the player had scrolled to.
   *
   * This method had two faults and they were the same fault. It replaced the
   * whole sheet unconditionally four times a second, and `.mg-body` — the box
   * that actually scrolls — went with it. Replacing the contents of a scrolling
   * box collapses its scroll height for an instant, which clamps `scrollTop` to
   * zero, and nothing puts it back. So the Powers tab, which is three and a
   * third thousand pixels of crowns in a six-hundred-pixel window, snapped to the
   * top a quarter of a second after every attempt to scroll it. It was not that
   * the tab lacked `overflow-y: auto`; it has always had it. It was that the tab
   * was being thrown away while the player was reading it.
   *
   * Both halves of the fix are here. `setHtml` means an idle Ledger does not
   * touch the DOM at all — most of these pages change once a second at most, and
   * a page nobody is changing should be a page nobody is rebuilding. `keepScroll`
   * covers the times it genuinely does change, so a number moving somewhere above
   * the fold no longer costs the player their place.
   */
  private draw(): void {
    const w = this.city;
    if (!w) return;
    const tabs = TABS.map(
      (t) =>
        `<div class="mg-tab${t.id === this.tab ? ' on' : ''}" data-act="tab" data-tab="${t.id}">${t.label}</div>`,
    ).join('');

    const body =
      this.tab === 'realm'
        ? realmTab(w)
        : this.tab === 'powers'
          ? powersTab(this.realm)
          : this.tab === 'people'
            ? peopleTab(w)
            : this.tab === 'research'
              ? researchTab(w)
              : productionTab(w);

    const here = TABS.find((t) => t.id === this.tab);
    const html =
      `<div class="mg-sheet">` +
      `<div class="mg-head">` +
      `<div><h1>The Ledger</h1><div class="mg-sub">${esc(here?.sub ?? '')} · ${esc(formatDateLong(w))}</div></div>` +
      `<div class="mg-x" data-act="close">Close <span class="mg-key">Esc</span></div>` +
      `</div>` +
      `<div class="mg-tabs">${tabs}</div>` +
      `<div class="mg-body">${body}</div>` +
      `</div>`;

    // A tab change is a different page, and a different page starts at the top.
    // Anything else is the same page with the numbers moved on, and keeps its
    // place.
    const turned = this.drawnTab !== this.tab;
    this.drawnTab = this.tab;
    keepScroll(this.el, turned ? [] : ['.mg-body'], () => setHtml(this.el, html));
  }

  /** Which tab the markup on screen belongs to. See `draw`. */
  private drawnTab: ManageTab | '' = '';
}

// ── Realm ──────────────────────────────────────────────────────────────────

/**
 * The purse.
 *
 * `w.econ` is recomputed every single tick and, before this screen existed, four
 * of its five fields appeared only inside the gold chip's tooltip. The dial
 * beneath it is the one economic lever the player holds, and until now nothing
 * in the game turned it — `onTaxPolicy` was an optional callback nobody
 * supplied, so the notches on the resource strip were a readout wearing the
 * clothes of a control.
 */
function realmTab(w: World): string {
  const e = w.econ;
  const pros = prosperity(w);
  const rows: string[] = [];

  rows.push(
    stat('Treasury', `${Math.floor(w.res.gold)}`, RESOURCE_GLYPHS.gold, e.net >= 0 ? 'good' : 'bad',
      `${signed(e.net, 1)} a minute`),
    stat('Tax', `+${e.taxIncome.toFixed(1)}`, '', 'good', `from ${w.pop} souls`),
    stat('Trade', `+${e.tradeIncome.toFixed(1)}`, '', 'good', 'markets and counting houses'),
    stat('Wages', `−${e.wageBill.toFixed(1)}`, '', 'bad', `${e.onPayroll} on the payroll`),
  );

  const dial = TAX_BANDS.map((band, i) => {
    const proj = taxProjection(w, i);
    const on = i === w.taxPolicy;
    return (
      `<div class="mg-band${on ? ' on' : ''}${i > 1 ? ' hard' : ''}" data-act="tax" data-a="${i}">` +
      `<div class="bn">${esc(band.name)}</div>` +
      `<div class="bv">${signed(proj, 1)}<span class="u"> gold/min</span></div>` +
      `<div class="bm">morale ${signed(band.morale * 100, 0)}%</div>` +
      `<div class="bf">${esc(band.note)}</div>` +
      `</div>`
    );
  }).join('');

  // The race is passed for the reason the resource strip passes it: the
  // one-argument form is the generic settlement's list and omits any private
  // currency, so the Ledger's stores page would have been the second screen
  // unable to show a mana race what it was spending.
  const stores = RESOURCE_KEYS.filter((k) => hudResources(w.age, w.raceId, w.res.souls, w.res).includes(k))
    .map((k) => {
      const cap = k === 'souls' ? w.resCap * 2 : UNCAPPED_RESOURCES.includes(k) ? Infinity : w.resCap;
      const frac = Number.isFinite(cap) ? Math.min(1, w.res[k] / Math.max(1, cap)) : 0;
      const sy = seasonYield(w, k);
      return (
        `<tr><td class="g">${RESOURCE_GLYPHS[k]}</td><td>${esc(RESOURCE_LABELS[k])}</td>` +
        `<td class="n">${Math.floor(w.res[k])}</td>` +
        `<td class="n dim">${Number.isFinite(cap) ? Math.floor(cap) : '—'}</td>` +
        `<td class="barcell">${Number.isFinite(cap) ? `<i style="width:${(frac * 100).toFixed(0)}%"></i>` : ''}</td>` +
        `<td class="n ${sy > 1 ? 'good' : sy < 1 ? 'bad' : 'dim'}">${sy === 1 ? '—' : `${signed((sy - 1) * 100, 0)}%`}</td></tr>`
      );
    })
    .join('');

  return (
    `<div class="mg-stats">${rows.join('')}</div>` +
    (w.arrears > 0
      ? `<div class="mg-warn">The purse has been short for <b>${w.arrears.toFixed(0)}s</b>. ` +
        `At sixty a soldier walks home, and word gets round.</div>`
      : '') +
    section(
      'The dial',
      `Taxes come off the people, not the ground — <b>${w.pop}</b> souls at the band you set, ` +
        `multiplied by how well the town is doing. Prosperity is <b>×${pros.toFixed(2)}</b> on the ` +
        `band's rate right now, out of morale (<b>${(w.morale * 100).toFixed(0)}%</b>) and how much ` +
        `of the diet is bread (<b>${(w.breadShare * 100).toFixed(0)}%</b>). It runs from ×0.35 to ×1.45.`,
    ) +
    `<div class="mg-dial">${dial}</div>` +
    section('The stores', `Held against what the warehouses can take, and what ${esc(season(w).name)} is doing to each.`) +
    `<table class="mg-table"><thead><tr><th></th><th>Good</th><th class="n">Held</th><th class="n">Cap</th><th></th><th class="n">Season</th></tr></thead>` +
    `<tbody>${stores}</tbody></table>`
  );
}

// ── Powers ─────────────────────────────────────────────────────────────────

/**
 * Diplomacy, which was entirely invisible.
 *
 * `diplomaticView` already sorted the crowns by how much they matter to you,
 * itemised what each thinks of you, and — the part worth the whole file —
 * returned a *sentence* for every action that is not available. So every button
 * here is either live or wearing its own reason, and there is no greying logic
 * in this file at all, which is exactly how it should be: a UI that works out
 * its own greying gets it subtly wrong.
 */
function powersTab(realm: World | null): string {
  const c = realm?.campaign;
  if (!c) {
    return (
      `<div class="mg-empty">This settlement answers to nobody and nobody answers to it.<br>` +
      `Start a campaign and the other crowns will have opinions.</div>`
    );
  }
  const me = c.playerNation;
  const offers = pendingOffers(c);
  const wars = warView(c, me);
  const crowns = diplomaticView(c, me);

  const offerHtml = offers
    .map(
      (o) =>
        `<div class="mg-offer"><div class="ot">${esc(offerText(c, o))}</div>` +
        `<div class="mg-btns">` +
        `<span class="mg-btn" data-act="answer" data-a="${o.id}" data-b="1">Accept</span>` +
        `<span class="mg-btn ghost" data-act="answer" data-a="${o.id}" data-b="0">Refuse</span>` +
        `</div>` +
        `<div class="of">Expires on turn ${o.expires}. Silence is an answer, and it costs the same as a refusal.</div>` +
        `</div>`,
    )
    .join('');

  const warHtml = wars
    .map((v) => {
      const terms = v.options
        .map(
          (o) =>
            `<span class="mg-btn ghost tiny" data-act="peace" data-a="${v.warId}" data-b="${o.term}" data-c="${o.province}">` +
            `${esc(o.label)}${o.cost > 0 ? ` · ${o.cost}` : ''}</span>`,
        )
        .join('');
      return (
        `<div class="mg-war"><div class="wn">War with <b>${esc(v.enemyName)}</b>` +
        `<span class="ws ${v.score >= 0 ? 'good' : 'bad'}">${signed(v.score, 0)}</span></div>` +
        `<div class="wd">${esc(CB_NAMES[v.cb] ?? 'No pretext')} · <b>${v.turns}</b> months · ` +
        `battles <b>${v.battlesWon}</b>–<b>${v.battlesLost}</b> · ground <b>${v.provincesTaken}</b>–<b>${v.provincesLost}</b></div>` +
        `<div class="mg-btns">${terms}</div></div>`
      );
    })
    .join('');

  const crownHtml = crowns
    .map((v) => {
      const tags: string[] = [];
      if (v.atWar) tags.push('<span class="tg war">At war</span>');
      if (v.allied) tags.push('<span class="tg ally">Allied</span>');
      if (v.married) tags.push('<span class="tg">Married</span>');
      if (v.theirSubjection > 0) tags.push(`<span class="tg ally">${esc(SUBJECT_NAMES[v.theirSubjection] ?? 'Sworn')}</span>`);
      if (v.liege >= 0 && v.theirSubjection === 0) tags.push('<span class="tg dim">Sworn elsewhere</span>');
      if (v.truceTurns > 0) tags.push(`<span class="tg dim">Truce ${v.truceTurns}m</span>`);

      const terms = v.terms
        .filter((t) => t.value !== 0)
        .map((t) => `<span class="op ${t.value > 0 ? 'good' : 'bad'}">${esc(t.label)} ${signed(t.value, 0)}</span>`)
        .join('');

      const cb = v.casusBelli[0] ?? 0;
      /**
       * Legality and acceptance are two different questions and the panel owes
       * the player both. `allianceBlocked` says whether you may *ask*;
       * `ALLIANCE_OPINION` is what they will say — a deterministic threshold, on
       * purpose, so that a relationship can be planned rather than retried.
       */
      const bar =
        !v.atWar && v.allianceBlocked === ''
          ? `<div class="cr-bar ${v.theirOpinion >= ALLIANCE_OPINION ? 'good' : 'dim'}">` +
            `They ally at <b>${signed(ALLIANCE_OPINION, 0)}</b>, marry at <b>${signed(MARRIAGE_OPINION, 0)}</b>` +
            (v.theirOpinion < ALLIANCE_OPINION && !v.married
              ? ` — a marriage is worth +20, which is usually the whole of the gap`
              : '') +
            `</div>`
          : '';
      const btn = (act: string, a: number, b: number, label: string, blocked: string) =>
        `<span class="mg-btn ghost tiny${blocked ? ' off' : ''}" data-act="${act}" data-a="${a}" data-b="${b}"` +
        ` title="${esc(blocked || label)}">${esc(label)}</span>` +
        (blocked ? `<span class="why">${esc(blocked)}</span>` : '');

      return (
        `<div class="mg-crown">` +
        `<div class="cr-top"><span class="cn">${esc(v.name)}</span>${tags.join('')}</div>` +
        `<div class="cr-num">` +
        `<span>They think <b class="${v.theirOpinion >= 0 ? 'good' : 'bad'}">${signed(v.theirOpinion, 0)}</b> of you</span>` +
        `<span class="dim">You think ${signed(v.yourOpinion, 0)} of them</span>` +
        `<span class="dim">${esc(v.confessionName)}</span>` +
        `<span class="dim">${v.provinces} provinces · ${v.strength} strength</span>` +
        `</div>` +
        (terms ? `<div class="cr-ops">${terms}</div>` : '') +
        bar +
        `<div class="mg-btns">` +
        btn('war', v.nation, cb, `Declare war — ${CB_NAMES[cb] ?? 'no pretext'}`, v.warBlocked) +
        btn('diplo', DiploAction.ProposeAlliance, v.nation, 'Propose alliance', v.allianceBlocked) +
        btn('diplo', DiploAction.ProposeMarriage, v.nation, 'Offer marriage', v.marriageBlocked) +
        (v.theirSubjection > 0
          ? btn('diplo', DiploAction.AnnexSubject, v.nation, 'Annex', v.annexBlocked) +
            btn('diplo', DiploAction.ReleaseSubject, v.nation, 'Release', '')
          : '') +
        (v.allied ? btn('diplo', DiploAction.BreakAlliance, v.nation, 'Break the alliance', '') : '') +
        `</div></div>`
      );
    })
    .join('');

  return (
    (offers.length > 0 ? section('On the table', 'Answer, or do not. Both are answers.') + offerHtml : '') +
    (wars.length > 0
      ? section('Wars', 'What you may demand is decided by the war score, not by asking nicely.') + warHtml
      : section('Wars', 'None. The realm is at peace with you, which is not the same as friendly.')) +
    section('The crowns', `${crowns.length} of them still hold ground. Wars first, then your sworn, then whoever is biggest.`) +
    crownHtml
  );
}

// ── People ─────────────────────────────────────────────────────────────────

/**
 * Exported for `tests/ui-people.test.ts`, which holds this page's numbers and
 * sentences against the simulation's own readers — the income panel's rule,
 * applied to the age bands before they can drift.
 */
export function peopleTab(w: World): string {
  const villagerCap = Math.max(6, Math.floor(w.popCap * VILLAGER_SHARE));
  const soldiers = Math.max(0, w.pop - w.counts.villagers);
  const demand = grainDemandPerMin(w);
  const target = moraleTarget(w);

  /**
   * Idle *hands*, not idle villagers. `assignJobs` strips everyone past
   * `jobHands` of any job — the cradle and the fire always stand idle by
   * construction — and `recount` books them into `counts.idleVillagers` all the
   * same. Quoting that count raw would call every child a job the player has
   * not built, so the dependants the ladder itself refused come off first, with
   * `jobHands` deciding how many they are. The same figure feeds the warning
   * and the Working-hands tile below, so the two cannot disagree.
   */
  const hands = jobHands(w, w.counts.villagers);
  const dependants = Math.max(0, w.counts.villagers - hands);
  const idleHands = Math.max(0, w.counts.idleVillagers - dependants);

  // Where the beds come from. Read off the layout rather than restated.
  const beds = new Map<number, number>();
  for (let i = 0; i < w.buildings.slots.high; i++) {
    if (w.buildings.slots.alive[i] === 0) continue;
    if (w.buildings.team[i] !== Team.Player || w.buildings.build[i] < 1) continue;
    const t = w.buildings.typeId[i];
    if (buildingType(t).popCap <= 0) continue;
    beds.set(t, (beds.get(t) ?? 0) + 1);
  }
  const bedRows = [...beds.entries()]
    .sort((a, b) => buildingType(b[0]).popCap * b[1] - buildingType(a[0]).popCap * a[1])
    .map(([t, n]) => {
      const def = buildingType(t);
      return (
        `<tr><td>${esc(def.name)}</td><td class="n">${n}</td>` +
        `<td class="n">${def.popCap} each</td><td class="n">${def.popCap * n}</td></tr>`
      );
    })
    .join('');

  const terms = moraleTerms(w)
    .filter((t) => Math.abs(t.value) > 0.0005)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .map((t) => {
      const pct = t.value * 100;
      const width = Math.min(100, Math.abs(pct) * 2.2);
      return (
        `<div class="mg-term"><span class="tl">${esc(t.label)}</span>` +
        `<span class="tbar"><i class="${t.value > 0 ? 'up' : 'down'}" style="width:${width.toFixed(0)}%"></i></span>` +
        `<span class="tv ${t.value > 0 ? 'good' : 'bad'}">${signed(pct, 0)}</span>` +
        `<span class="tn dim">${esc(t.note)}</span></div>`
      );
    })
    .join('');

  return (
    `<div class="mg-stats">` +
    stat('Population', `${w.pop}`, '☗', w.pop >= w.popCap ? 'bad' : '', `of ${w.popCap} beds`) +
    stat('Villagers', `${w.counts.villagers}`, '', w.counts.villagers >= villagerCap ? 'bad' : '',
      `of ${villagerCap} — the rest of the cap is held for soldiers`) +
    stat('Under arms', `${soldiers}`, '', '', `${w.econ.onPayroll} drawing a wage`) +
    stat('Fed', `${(w.fedFraction * 100).toFixed(0)}%`, '', w.fedFraction < 0.99 ? 'bad' : 'good',
      `${(w.breadShare * 100).toFixed(0)}% of it on bread`) +
    `</div>` +
    (idleHands > 0
      ? `<div class="mg-warn">${idleHands} pairs of hands standing idle. Every one of them is a job you have not built.</div>`
      : '') +
    yearsSection(w, hands, idleHands) +
    section(
      'The table',
      `The town eats <b>${demand.toFixed(1)}</b> grain a minute at ${esc(season(w).name)} rates` +
        (seasonUpkeep(w) !== 1 ? ` — <b>${signed((seasonUpkeep(w) - 1) * 100, 0)}%</b> on the ordinary year` : '') +
        `. Bread is eaten first and is worth two and a half times its weight, which is the whole reason to own an oven.`,
    ) +
    `<div class="mg-stats">` +
    /**
     * The larder, shelf by shelf, and then how long it lasts.
     *
     * Two tiles used to say grain and bread; there are four kinds now and the
     * useful number was never the count of one of them but how many minutes the
     * *whole* larder covers, which is the number that moved when population
     * started eating in rungs. Fish and salt meat appear only when the town has
     * some, on the same rule the strip uses — a tile pinned at zero all run is
     * noise wearing a number.
     */
    stat('Grain held', `${Math.floor(w.res.grain)}`, RESOURCE_GLYPHS.grain, w.res.grain < 40 ? 'bad' : '',
      `${(w.res.grain / Math.max(0.01, demand)).toFixed(0)} minutes of it`) +
    stat('Bread held', `${Math.floor(w.res.bread)}`, RESOURCE_GLYPHS.bread, '',
      `${((w.res.bread * 2.5) / Math.max(0.01, demand)).toFixed(0)} minutes of it`) +
    (w.res.fish > 0
      ? stat('Fish held', `${Math.floor(w.res.fish)}`, RESOURCE_GLYPHS.fish, '',
          `${((w.res.fish * FOOD_VALUE.fish) / Math.max(0.01, demand)).toFixed(0)} minutes of it`)
      : '') +
    (w.res.meat > 0
      ? stat('Salt meat held', `${Math.floor(w.res.meat)}`, RESOURCE_GLYPHS.meat, '',
          `${((w.res.meat * FOOD_VALUE.meat) / Math.max(0.01, demand)).toFixed(0)} minutes of it`)
      : '') +
    stat('The whole larder', `${(larderWorth(w) / Math.max(0.01, demand)).toFixed(0)} min`,
      RESOURCE_GLYPHS.grain, larderWorth(w) < demand * 2 ? 'bad' : '',
      `${Math.floor(larderWorth(w))} in grain-equivalents against ${demand.toFixed(1)}/min of appetite`) +
    stat('A varied table', `${Math.round(w.dietVariety * 100)}%`, RESOURCE_GLYPHS.meat, '',
      'Four kinds is a feast; one is a ration. Worth a little prosperity.') +
    `</div>` +
    section('Where they sleep', `Beds are the ceiling on everything. A company needs them as much as a family does.`) +
    (bedRows
      ? `<table class="mg-table"><thead><tr><th>Building</th><th class="n">Standing</th><th class="n">Beds</th><th class="n">Total</th></tr></thead><tbody>${bedRows}</tbody></table>`
      : `<div class="mg-empty">Nothing but the Hearth.</div>`) +
    section(
      'Mood',
      `<b>${(w.morale * 100).toFixed(0)}%</b> now, heading for <b>${(target * 100).toFixed(0)}%</b>. ` +
        `Morale moves slowly and it moves everything: tax yield, how fast the town grows, and whether the line holds.`,
    ) +
    `<div class="mg-terms">${terms}</div>`
  );
}

/**
 * The age bands, which until this section had no readout anywhere: the muster
 * could refuse a company for want of grown hands and no screen showed the
 * hands.
 *
 * Every number is quoted from `systems/demography.ts`' own readers —
 * `demographySnapshot`, `jobHands`, `musterAdultRefusal` — and nothing is
 * re-derived, on the income panel's hard-learned rule that a readout which
 * works the figure out for itself agrees with the bug. The muster warning goes
 * one further: it prints `musterRoom`'s sentence, and only when that sentence
 * *is* the adult refusal — so the line read here and the refusal the muster
 * button gives are one string, and the peoples whose bindings are exempt from
 * the gate (the pool, the vault, the pens, the warren) are never warned about
 * a gate that never binds them.
 */
function yearsSection(w: World, hands: number, idleHands: number): string {
  const snap = demographySnapshot(w);
  if (snap.ageless) {
    // The Pale Litany. Zero children here is the design, not a famine, and it
    // gets a sentence rather than a row of empty tiles.
    return section(
      'The years',
      `The cycle passes over this people. Nobody here was born and nobody is wearing out — ` +
        `every head the town holds was raised grown, and all <b>${w.counts.villagers}</b> of the villagers can work.`,
    );
  }

  const isMoot = race(w.raceId).binding === 'moot';
  const roll = Math.max(1, w.pop);
  const share = (n: number) => `${Math.round((n / roll) * 100)}%`;

  /**
   * Would the adult gate refuse the next company? Asked of the door itself:
   * for each company the roster can currently field, `musterRoom` — the one
   * rule `recruitCompany` applies — is compared against `musterAdultRefusal`'s
   * sentence, which is the same for every refused size. A tech-locked or
   * age-locked company is not "next" and is skipped, the way the drill list
   * skips it.
   */
  let refusal = '';
  for (const id of w.faction.roster) {
    const idx = unitTypeIndex(id);
    const t = unitType(idx);
    if (t.tier > Math.max(1, w.age)) continue;
    if (t.tech && !w.techNodes.includes(t.tech)) continue;
    const adult = musterAdultRefusal(w, t.companySize);
    if (adult !== '' && musterRoom(w, idx, t.companySize).why === adult) {
      refusal = adult;
      break;
    }
  }

  return (
    section(
      'The years',
      `Settlers arrive grown — they walked here — but the town's own children spend ` +
        `<b>${CHILDHOOD_MONTHS}</b> months in the cradle, work for <b>${ADULT_MONTHS}</b>, ` +
        `and sit <b>${ELDER_MONTHS}</b> more at the fire, one with another. ` +
        (isMoot
          ? `The warren is passed over at the door: a goblin is born knowing how to carry, so every head works whatever its years.`
          : `Both ends of a life are a bill — the ladder employs only the grown.`),
    ) +
    `<div class="mg-stats">` +
    stat('Children', `${snap.children}`, '', '',
      isMoot
        ? `${share(snap.children)} of the roll — in the cradle ${CHILDHOOD_MONTHS} months, and carrying from the first`
        : `${share(snap.children)} of the roll — too young for the ladder; on it in ${CHILDHOOD_MONTHS} months, one with another`) +
    stat('Grown', `${snap.adults}`, '', refusal ? 'bad' : '',
      `${share(snap.adults)} of the roll, soldiers included — a working life runs ${ADULT_MONTHS} months`) +
    stat('At the fire', `${snap.elders}`, '', '',
      isMoot
        ? `${share(snap.elders)} — old, and carrying anyway; ${ELDER_MONTHS} months at the fire, one with another`
        : `${share(snap.elders)} — done with the ladder; ${ELDER_MONTHS} months at the fire, one with another`) +
    stat('Working hands', `${hands}`, '', idleHands > 0 ? 'bad' : '',
      idleHands > 0
        ? `of ${w.counts.villagers} villagers — ${idleHands} of them standing idle`
        : `of ${w.counts.villagers} villagers — every one of them on a shift or a scaffold`) +
    `</div>` +
    (refusal !== ''
      ? `<div class="mg-warn">${esc(refusal)} The muster stretches ${MUSTER_GRACE_HEADS} heads past the grown and no further; the next company waits on the cradle.</div>`
      : '')
  );
}

// ── Production ─────────────────────────────────────────────────────────────

/**
 * Every chain, and what is choking it.
 *
 * One `productionReadout` per live building, folded by type. The throttle is
 * the point of the tab: a Smeltery at 3% because there is no iron looks exactly
 * like a Smeltery working, on the map and on every panel that existed before
 * this one.
 */
function productionTab(w: World): string {
  interface Group {
    typeId: number;
    count: number;
    workers: number;
    slots: number;
    unbuilt: number;
    /** How many of them are short of an input, and of which. */
    starved: Map<ResourceKey, number>;
    outputs: Map<ResourceKey, { now: number; best: number }>;
    inputs: Map<ResourceKey, number>;
    place: number;
  }
  const groups = new Map<number, Group>();
  const net = new Map<ResourceKey, number>();
  const b = w.buildings;

  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    const def = buildingType(b.typeId[i]);
    const makes = RESOURCE_KEYS.some((k) => (def.produces[k] ?? 0) > 0 || (def.consumes[k] ?? 0) > 0);
    if (!makes) continue;
    let g = groups.get(b.typeId[i]);
    if (!g) {
      g = {
        typeId: b.typeId[i],
        count: 0,
        workers: 0,
        slots: 0,
        unbuilt: 0,
        starved: new Map(),
        outputs: new Map(),
        inputs: new Map(),
        place: 0,
      };
      groups.set(b.typeId[i], g);
    }
    g.count++;
    if (b.build[i] < 1) {
      g.unbuilt++;
      continue;
    }
    const r = productionReadout(w, i);
    g.workers += r.workers;
    g.slots += r.workerSlots;
    g.place += r.placeMul;
    if (r.starvedOf !== '') g.starved.set(r.starvedOf, (g.starved.get(r.starvedOf) ?? 0) + 1);
    for (const o of r.outputs) {
      const e = g.outputs.get(o.k) ?? { now: 0, best: 0 };
      e.now += o.perMin;
      e.best += o.bestPerMin;
      g.outputs.set(o.k, e);
      net.set(o.k, (net.get(o.k) ?? 0) + o.perMin);
    }
    for (const inp of r.inputs) {
      g.inputs.set(inp.k, (g.inputs.get(inp.k) ?? 0) + inp.perMin);
      net.set(inp.k, (net.get(inp.k) ?? 0) - inp.perMin);
    }
  }

  /**
   * The town's own appetite is the one draw that is not a building — and
   * since the diet grew two more kinds it is not a draw on one good either.
   * `foodDrawPerMin` runs the same richest-first larder `stepUpkeep` runs, so
   * the row a fishing town reads is fish and the row a farming town reads is
   * grain, rather than both reading grain and one of them being a lie.
   */
  for (const [k, v] of foodDrawPerMin(w)) net.set(k, (net.get(k) ?? 0) - v);

  const rows = [...groups.values()]
    .sort((x, y) => BUILDING_TYPES.indexOf(buildingType(x.typeId)) - BUILDING_TYPES.indexOf(buildingType(y.typeId)))
    .map((g) => {
      const def = buildingType(g.typeId);
      const built = g.count - g.unbuilt;
      const outs = [...g.outputs.entries()]
        .map(([k, v]) => {
          const pct = v.best > 0 ? v.now / v.best : 1;
          return (
            `<span class="flow out ${pct < 0.6 ? 'bad' : pct < 0.95 ? 'mid' : 'good'}">` +
            `${RESOURCE_GLYPHS[k]} ${v.now.toFixed(1)}<span class="of"> / ${v.best.toFixed(1)}</span></span>`
          );
        })
        .join('');
      const ins = [...g.inputs.entries()]
        .map(([k, v]) => `<span class="flow in">${RESOURCE_GLYPHS[k]} ${v.toFixed(1)}</span>`)
        .join('');
      const starve = [...g.starved.entries()]
        .map(
          ([k, n]) =>
            `<div class="mg-choke"><b>${n} of ${built}</b> throttled — no ${esc(RESOURCE_LABELS[k])}. ` +
            `Held: ${Math.floor(w.res[k])}.</div>`,
        )
        .join('');
      const place = built > 0 ? g.place / built : 1;
      const staffed = g.slots > 0 ? g.workers / g.slots : 1;
      return (
        `<div class="mg-chain">` +
        `<div class="ch-top"><span class="cn">${esc(def.name)}</span>` +
        `<span class="cc">${built}${g.unbuilt > 0 ? ` <span class="dim">+${g.unbuilt} building</span>` : ''}</span></div>` +
        `<div class="ch-flow">${ins}${ins && outs ? '<span class="arrow">→</span>' : ''}${outs}` +
        `<span class="dim per">/min</span></div>` +
        `<div class="ch-mul">` +
        (g.slots > 0
          ? `<span class="${staffed < 0.99 ? 'bad' : 'good'}">Staffed ${g.workers}/${g.slots}</span>`
          : `<span class="dim">Needs nobody</span>`) +
        `<span class="${place > 1.001 ? 'good' : place < 0.999 ? 'bad' : 'dim'}">Placement ×${place.toFixed(2)}</span>` +
        `</div>` +
        starve +
        `</div>`
      );
    })
    .join('');

  const netRows = RESOURCE_KEYS.filter((k) => net.has(k) && Math.abs(net.get(k) as number) > 0.005)
    .map((k) => {
      const v = net.get(k) as number;
      return (
        `<tr><td class="g">${RESOURCE_GLYPHS[k]}</td><td>${esc(RESOURCE_LABELS[k])}</td>` +
        `<td class="n ${v >= 0 ? 'good' : 'bad'}">${signed(v, 1)}</td>` +
        `<td class="n dim">${Math.floor(w.res[k])} held</td>` +
        `<td class="dim">${v < 0 ? `${(w.res[k] / Math.max(0.01, -v)).toFixed(0)} minutes left` : ''}</td></tr>`
      );
    })
    .join('');

  return (
    section(
      'The balance',
      'Everything the settlement makes, less everything it eats — including the mouths, which are the largest single draw in the game.',
    ) +
    (netRows
      ? `<table class="mg-table"><thead><tr><th></th><th>Good</th><th class="n">Net/min</th><th class="n">Held</th><th></th></tr></thead><tbody>${netRows}</tbody></table>`
      : `<div class="mg-empty">Nothing is running yet.</div>`) +
    section('The chains', 'Made, and made of. The second number on each output is what it would make fully staffed and fully fed.') +
    (rows || `<div class="mg-empty">No producers standing.</div>`)
  );
}

// ── Bits ───────────────────────────────────────────────────────────────────

function section(title: string, sub = ''): string {
  return `<h2 class="mg-sec">${esc(title)}</h2>` + (sub ? `<div class="mg-secsub">${sub}</div>` : '');
}

function stat(label: string, value: string, glyph: string, tone: string, note: string): string {
  return (
    `<div class="mg-stat"><div class="sl">${esc(label)}</div>` +
    `<div class="sv ${tone}">${glyph ? `<span class="sg">${glyph}</span>` : ''}${esc(value)}</div>` +
    `<div class="sn">${esc(note)}</div></div>`
  );
}

function signed(v: number, places: number): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(places)}`;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string);
}

let styled = false;
function injectManageStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'manage-style';
  s.textContent = `
    #manage { position: fixed; inset: 0; z-index: 75; display: none;
      align-items: center; justify-content: center; background: rgba(6,6,7,0.84);
      font-family: Inter, system-ui, sans-serif; pointer-events: auto; }
    #manage .mg-sheet { width: min(1180px, 94vw); height: min(88vh, 900px);
      display: flex; flex-direction: column;
      background: rgba(18,16,14,0.97); border: 1px solid rgba(200,164,76,0.30);
      box-shadow: 0 30px 90px rgba(0,0,0,0.85); }
    #manage .mg-head { display: flex; align-items: flex-start; justify-content: space-between;
      padding: 18px 24px 12px; border-bottom: 1px solid rgba(200,164,76,0.18); }
    #manage h1 { font-family: Cinzel, Georgia, serif; font-size: 19px; letter-spacing: 0.26em;
      text-transform: uppercase; color: ${toCss(C.gold)}; margin: 0; }
    #manage .mg-sub { font-size: 12px; color: ${toCss(C.boneDeep)}; font-style: italic; margin-top: 5px; }
    #manage .mg-x { cursor: pointer; font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
      color: ${toCss(C.boneDim)}; padding: 6px 12px; border: 1px solid rgba(200,164,76,0.22); }
    #manage .mg-x:hover { color: ${toCss(C.goldPale)}; background: rgba(200,164,76,0.12); }
    #manage .mg-key { font-size: 9.5px; color: ${toCss(C.boneDeep)}; border: 1px solid rgba(217,207,188,0.22);
      padding: 1px 5px; margin-left: 6px; }

    #manage .mg-tabs { display: flex; gap: 2px; padding: 10px 24px 0; }
    #manage .mg-tab { padding: 8px 20px; cursor: pointer; font-family: Cinzel, Georgia, serif;
      font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: ${toCss(C.boneDeep)};
      background: rgba(255,255,255,0.03);
      clip-path: polygon(7px 0, 100% 0, calc(100% - 7px) 100%, 0 100%); }
    #manage .mg-tab:hover { color: ${toCss(C.bone)}; background: rgba(200,164,76,0.10); }
    #manage .mg-tab.on { color: ${toCss(C.goldPale)}; background: rgba(200,164,76,0.18); }

    /* The Powers tab is 3,310px of crowns in a 677px window, and the sheet's
       bottom edge cuts whichever card it lands on. The shadow says that edge is
       a fold rather than an ending — see the note beside the same four gradients
       in style.ts for why this is paint and not a scroll listener. The cover
       colour is .mg-sheet's, minus its alpha. */
    #manage .mg-body { flex: 1; overflow-y: auto; padding: 4px 24px 28px;
      background-image:
        linear-gradient(to bottom, rgb(18,16,14) 64%, rgba(18,16,14,0)),
        linear-gradient(to top, rgb(18,16,14) 64%, rgba(18,16,14,0)),
        linear-gradient(to bottom, rgba(200,164,76,0.26), rgba(200,164,76,0)),
        linear-gradient(to top, rgba(200,164,76,0.26), rgba(200,164,76,0));
      background-position: 0 0, 0 100%, 0 0, 0 100%;
      background-size: 100% 26px, 100% 26px, 100% 16px, 100% 16px;
      background-repeat: no-repeat;
      background-attachment: local, local, scroll, scroll; }
    /* The Study sheet. Four columns of cards, one per branch, and they wrap
       rather than shrink: a tech card that is narrower than its rules line is
       a card nobody reads. The whole grid scrolls inside .mg-body like every
       other tab, so a race with a deep book is not a layout problem. */
    #manage .mg-techhead { font-size: 12px; color: ${toCss(C.bone)}; margin: 4px 0 14px;
      padding: 9px 12px; border-left: 2px solid ${toCss(C.gold)};
      background: rgba(200,164,76,0.08); line-height: 1.6; }
    #manage .mg-techhead b { color: ${toCss(C.goldPale)}; font-weight: 500; }
    #manage .mg-techhead.none { border-left-color: ${toCss(C.oxbloodLight)};
      background: rgba(168,48,47,0.1); color: ${toCss(C.oxbloodLight)}; }
    #manage .mg-tech-grid { display: grid; gap: 16px;
      grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); align-items: start; }
    #manage .mg-techcol h2 { font-family: 'Cinzel', Georgia, serif; font-size: 12px;
      letter-spacing: 0.18em; text-transform: uppercase; color: ${toCss(C.gold)};
      margin: 0 0 2px; font-weight: 500; }
    #manage .mg-technote { font-size: 10.5px; font-style: italic; line-height: 1.5;
      color: ${toCss(C.boneDeep)}; margin-bottom: 10px; }
    #manage .mg-tech { padding: 8px 10px; margin-bottom: 7px;
      border: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.022); }
    #manage .mg-tech .tn { font-family: 'Cinzel', Georgia, serif; font-size: 11.5px;
      letter-spacing: 0.06em; color: ${toCss(C.gold)}; display: flex;
      justify-content: space-between; align-items: baseline; gap: 8px; }
    #manage .mg-tech .ta { font-family: inherit; font-size: 9px; letter-spacing: 0.14em;
      color: ${toCss(C.boneDeep)}; flex: none; }
    #manage .mg-tech .tr { font-size: 11px; line-height: 1.45; margin-top: 4px;
      color: ${toCss(C.bone)}; }
    /* The refusal sentence, or the price. This line is the point of the card:
       a node that is dim and silent is the failure the Academy gate would
       otherwise have introduced. */
    #manage .mg-tech .tm { font-size: 10px; letter-spacing: 0.1em; margin-top: 6px;
      color: ${toCss(C.boneDeep)}; }
    #manage .mg-tech .tp { font-size: 10.5px; margin-top: 3px; color: ${toCss(C.goldPale)};
      font-variant-numeric: tabular-nums; }
    #manage .mg-tech .tf { font-size: 10px; font-style: italic; line-height: 1.4;
      margin-top: 6px; color: ${toCss(C.boneDeep)}; }
    #manage .mg-tech.owned { border-color: rgba(125,148,54,0.45); background: rgba(125,148,54,0.09); }
    #manage .mg-tech.owned .tm { color: ${toCss(C.plague)}; letter-spacing: 0.14em;
      text-transform: uppercase; }
    #manage .mg-tech.open { cursor: pointer; border-color: ${toCss(C.gold)};
      background: rgba(200,164,76,0.1); }
    #manage .mg-tech.open:hover { background: rgba(200,164,76,0.2); }
    #manage .mg-tech.open .tm { color: ${toCss(C.goldPale)}; letter-spacing: 0.14em;
      text-transform: uppercase; }
    #manage .mg-tech.shut { opacity: 0.62; }
    #manage .mg-tech.shut .tm { color: ${toCss(C.oxbloodLight)}; font-style: italic;
      letter-spacing: 0.02em; }
    #manage .dim { color: ${toCss(C.boneDeep)}; }
    #manage .good { color: ${toCss(C.plague)}; }
    #manage .bad { color: ${toCss(C.oxbloodLight)}; }
    #manage .mid { color: ${toCss(C.gold)}; }
    #manage .n { text-align: right; font-variant-numeric: tabular-nums; }

    #manage .mg-sec { font-family: Cinzel, Georgia, serif; font-size: 13px; letter-spacing: 0.14em;
      text-transform: uppercase; color: ${toCss(C.goldPale)}; margin: 24px 0 6px;
      border-bottom: 1px solid rgba(200,164,76,0.18); padding-bottom: 5px; }
    #manage .mg-secsub { font-size: 11.5px; color: ${toCss(C.boneDeep)}; line-height: 1.55;
      margin-bottom: 12px; max-width: 88ch; }
    #manage .mg-secsub b { color: ${toCss(C.bone)}; font-weight: 500; }
    #manage .mg-empty { font-size: 12px; color: ${toCss(C.boneDeep)}; font-style: italic;
      padding: 16px 2px; line-height: 1.6; }
    #manage .mg-warn { font-size: 12px; color: ${toCss(C.oxbloodLight)}; line-height: 1.5;
      margin: 14px 0 0; padding: 9px 12px; background: rgba(110,31,34,0.18);
      border-left: 2px solid ${toCss(C.oxbloodLight)}; }

    #manage .mg-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px; margin-top: 16px; }
    #manage .mg-stat { padding: 11px 13px; background: rgba(0,0,0,0.32);
      border: 1px solid rgba(200,164,76,0.14); }
    #manage .mg-stat .sl { font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
      color: ${toCss(C.boneDeep)}; }
    #manage .mg-stat .sv { font-size: 25px; font-variant-numeric: tabular-nums; margin: 3px 0 2px;
      color: ${toCss(C.bone)}; }
    #manage .mg-stat .sv .sg { font-size: 16px; color: ${toCss(C.goldDeep)}; margin-right: 5px; }
    #manage .mg-stat .sn { font-size: 10.5px; color: ${toCss(C.boneDeep)}; line-height: 1.4; }

    #manage .mg-table { width: 100%; border-collapse: collapse; font-size: 12px; }
    #manage .mg-table th { text-align: left; font-size: 9.5px; letter-spacing: 0.16em;
      text-transform: uppercase; color: ${toCss(C.boneDeep)}; font-weight: 400;
      padding: 4px 8px; border-bottom: 1px solid rgba(200,164,76,0.16); }
    #manage .mg-table th.n { text-align: right; }
    #manage .mg-table td { padding: 5px 8px; border-bottom: 1px solid rgba(255,255,255,0.04); }
    #manage .mg-table td.g { color: ${toCss(C.goldDeep)}; width: 18px; }
    #manage .mg-table .barcell { width: 130px; background: rgba(0,0,0,0.4); padding: 0; height: 6px; }
    #manage .mg-table .barcell i { display: block; height: 22px; background: rgba(200,164,76,0.35); }

    #manage .mg-dial { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
    #manage .mg-band { padding: 12px 12px 14px; cursor: pointer; background: rgba(255,255,255,0.025);
      border: 1px solid rgba(200,164,76,0.16); transition: background 120ms, border-color 120ms; }
    #manage .mg-band:hover { background: rgba(200,164,76,0.13); border-color: ${toCss(C.gold)}; }
    #manage .mg-band.on { background: rgba(200,164,76,0.20); border-color: ${toCss(C.goldPale)}; }
    #manage .mg-band.hard.on { background: rgba(110,31,34,0.28); border-color: ${toCss(C.oxbloodLight)}; }
    #manage .mg-band .bn { font-family: Cinzel, Georgia, serif; font-size: 12px; letter-spacing: 0.12em;
      color: ${toCss(C.gold)}; }
    #manage .mg-band.hard .bn { color: ${toCss(C.oxbloodLight)}; }
    #manage .mg-band .bv { font-size: 17px; font-variant-numeric: tabular-nums; margin-top: 5px; }
    #manage .mg-band .bv .u { font-size: 10px; color: ${toCss(C.boneDeep)}; }
    #manage .mg-band .bm { font-size: 10.5px; color: ${toCss(C.boneDim)}; margin-top: 2px; }
    #manage .mg-band .bf { font-size: 10.5px; color: ${toCss(C.boneDeep)}; font-style: italic;
      margin-top: 7px; line-height: 1.4; }

    #manage .mg-term { display: grid; grid-template-columns: 190px 120px 46px 1fr; align-items: center;
      gap: 10px; padding: 4px 0; font-size: 11.5px; }
    #manage .mg-term .tl { color: ${toCss(C.bone)}; }
    #manage .mg-term .tbar { height: 7px; background: rgba(0,0,0,0.45); display: block; position: relative; }
    #manage .mg-term .tbar i { display: block; height: 100%; }
    #manage .mg-term .tbar i.up { background: ${toCss(C.plague)}; }
    #manage .mg-term .tbar i.down { background: ${toCss(C.oxbloodLight)}; }
    #manage .mg-term .tv { text-align: right; font-variant-numeric: tabular-nums; }
    #manage .mg-term .tn { font-size: 10.5px; font-style: italic; }

    #manage .mg-chain { padding: 11px 13px; margin-bottom: 7px; background: rgba(255,255,255,0.022);
      border-left: 2px solid rgba(200,164,76,0.35); }
    #manage .mg-chain .ch-top { display: flex; justify-content: space-between; align-items: baseline; }
    #manage .mg-chain .cn { font-size: 13px; color: ${toCss(C.bone)}; }
    #manage .mg-chain .cc { font-size: 11px; color: ${toCss(C.gold)}; font-variant-numeric: tabular-nums; }
    #manage .mg-chain .ch-flow { display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
      margin-top: 6px; font-size: 12.5px; font-variant-numeric: tabular-nums; }
    #manage .mg-chain .flow.in { color: ${toCss(C.boneDim)}; }
    #manage .mg-chain .flow .of { font-size: 10.5px; color: ${toCss(C.boneDeep)}; }
    #manage .mg-chain .arrow { color: ${toCss(C.goldDeep)}; }
    #manage .mg-chain .per { font-size: 10px; }
    #manage .mg-chain .ch-mul { display: flex; gap: 16px; margin-top: 5px; font-size: 10.5px; }
    #manage .mg-choke { margin-top: 6px; font-size: 11px; color: ${toCss(C.oxbloodLight)};
      background: rgba(110,31,34,0.16); padding: 5px 8px; }

    #manage .mg-crown, #manage .mg-war, #manage .mg-offer {
      padding: 11px 13px; margin-bottom: 7px; background: rgba(255,255,255,0.022);
      border-left: 2px solid rgba(200,164,76,0.30); }
    #manage .mg-war { border-left-color: ${toCss(C.oxbloodLight)}; }
    #manage .mg-offer { border-left-color: ${toCss(C.gold)}; background: rgba(200,164,76,0.08); }
    #manage .mg-offer .ot { font-size: 13px; color: ${toCss(C.bone)}; }
    #manage .mg-offer .of { font-size: 10.5px; color: ${toCss(C.boneDeep)}; font-style: italic; margin-top: 4px; }
    #manage .cr-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    #manage .cr-top .cn { font-family: Cinzel, Georgia, serif; font-size: 13.5px; letter-spacing: 0.08em;
      color: ${toCss(C.bone)}; }
    #manage .tg { font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase;
      padding: 2px 7px; border: 1px solid rgba(217,207,188,0.2); color: ${toCss(C.boneDim)}; }
    #manage .tg.war { color: ${toCss(C.oxbloodLight)}; border-color: rgba(160,50,45,0.55); }
    #manage .tg.ally { color: ${toCss(C.plague)}; border-color: rgba(125,148,54,0.5); }
    #manage .cr-num { display: flex; flex-wrap: wrap; gap: 18px; font-size: 11.5px; margin-top: 6px;
      color: ${toCss(C.boneDim)}; }
    #manage .cr-bar { font-size: 10.5px; margin-top: 6px; }
    #manage .cr-bar b { font-variant-numeric: tabular-nums; }
    #manage .cr-ops { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
    #manage .op { font-size: 10px; padding: 1px 6px; background: rgba(0,0,0,0.35);
      font-variant-numeric: tabular-nums; }
    #manage .mg-war .wn { font-size: 13px; color: ${toCss(C.bone)}; }
    #manage .mg-war .ws { margin-left: 10px; font-variant-numeric: tabular-nums; }
    #manage .mg-war .wd { font-size: 11px; color: ${toCss(C.boneDim)}; margin-top: 4px; }

    #manage .mg-btns { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 8px; }
    #manage .mg-btn { display: inline-flex; align-items: center; padding: 5px 12px; cursor: pointer;
      font-family: Cinzel, Georgia, serif; font-size: 10.5px; letter-spacing: 0.12em;
      text-transform: uppercase; color: ${toCss(C.bone)};
      background: linear-gradient(180deg, rgba(110,31,34,0.95), rgba(70,18,20,0.95));
      border: 1px solid rgba(200,164,76,0.35);
      clip-path: polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%); }
    #manage .mg-btn.ghost { background: linear-gradient(180deg, rgba(40,37,33,0.9), rgba(22,20,18,0.9));
      border-color: rgba(200,164,76,0.22); }
    #manage .mg-btn.tiny { font-size: 10px; padding: 4px 9px; }
    #manage .mg-btn:hover { filter: brightness(1.35); }
    #manage .mg-btn.off { filter: grayscale(0.85) brightness(0.5); cursor: not-allowed; }
    #manage .why { font-size: 10.5px; color: ${toCss(C.boneDeep)}; font-style: italic; margin-right: 6px; }
  `;
  document.head.appendChild(s);
}

// ── Study ──────────────────────────────────────────────────────────────────

/**
 * The tech tree, which until now no player could reach.
 *
 * `Cmd.Research` was handled in `apply.ts` and sent by nothing: sixty shared
 * nodes, forty-four race nodes and the Academy gate in front of them were all
 * reachable only from the debug console. This is the door.
 *
 * Three rules it follows, and each is a mistake that was available here:
 *
 *  - **`techFor(w.raceId)`, never `ALL_TECH_NODES`.** The latter would show a
 *    Pale Litany player the dwarves' Book of Grudges, priced in a currency they
 *    cannot hold; `TECH_NODES` is the other error and omits the race column
 *    entirely, which is the half of the tree a people's identity lives in.
 *  - **`researchError` decides what is greyed, and supplies the words.** The
 *    whole value of gating tech behind a building is lost if the node simply
 *    goes dim and says nothing, so the sentence is printed verbatim. It is the
 *    same rule `doResearch` enforces, so the reason shown and the refusal given
 *    cannot drift.
 *  - **The fourth heading comes from the race**, `race(w.raceId).branchName`.
 *    `BRANCH_NAMES.race` is a fallback for a caller with no race in hand and
 *    reads "Your Own", which is nobody's name for anything.
 *
 * Laid out by `row` within each branch rather than on the `row`/`col` grid the
 * data also carries: a four-column sheet is already narrow, and a branch whose
 * nodes sit at columns 0 and 2 would open a hole in it. Depth is the thing worth
 * showing, and depth is `row`.
 */
function researchTab(w: World): string {
  const depth = academyTier(w);
  const nodes = techFor(w.raceId);
  const mine = race(w.raceId);

  const branches: Array<{ id: TechBranch; name: string; note: string }> = [
    { id: 'fortification', name: BRANCH_NAMES.fortification, note: 'Stone, and what stands behind it' },
    { id: 'warhost', name: BRANCH_NAMES.warhost, note: 'Men, and what they carry' },
    { id: 'sacrament', name: BRANCH_NAMES.sacrament, note: 'What is asked for, and what it costs' },
    { id: 'race', name: mine.branchName, note: mine.branchFlavour },
  ];

  const columns = branches
    .map((br) => {
      const own = nodes.filter((t) => t.branch === br.id).sort((a, b) => a.row - b.row || a.col - b.col);
      if (own.length === 0) return '';
      const cards = own
        .map((t) => {
          const known = w.techNodes.includes(t.id);
          const why = known ? '' : researchError(w, t.id);
          const state = known ? 'owned' : why === '' ? 'open' : 'shut';
          const price = RESOURCE_KEYS.filter((k) => (t.cost[k] ?? 0) > 0)
            .map((k) => `${RESOURCE_GLYPHS[k]}${Math.round(t.cost[k] ?? 0)}`)
            .join(' ');
          return (
            `<div class="mg-tech ${state}"${why === '' && !known ? ` data-act="study" data-id="${esc(t.id)}"` : ''}>` +
            `<div class="tn">${esc(t.name)}<span class="ta">Age ${t.age}</span></div>` +
            `<div class="tr">${esc(t.rules)}</div>` +
            // Three states, three different sentences on one line: what you
            // already have, what clicking will do, or why it will not. The
            // price is a separate line under the action rather than standing in
            // for it — a card that says only "60 stone" does not tell a player
            // it is a thing they may press.
            `<div class="tm">${known ? 'Known' : why === '' ? 'Study it' : esc(why)}</div>` +
            (known || why !== '' ? '' : `<div class="tp">${esc(price)}</div>`) +
            `<div class="tf">${esc(t.flavour)}</div>` +
            `</div>`
          );
        })
        .join('');
      return `<div class="mg-techcol"><h2>${esc(br.name)}</h2><div class="mg-technote">${esc(br.note)}</div>${cards}</div>`;
    })
    .join('');

  /**
   * The Academy line, which is the first thing to read and often the only thing
   * that matters. A settlement with no Academy can buy nothing at all, and that
   * is a sentence rather than sixty grey cards.
   */
  const head =
    depth === 0
      ? `<div class="mg-techhead none">There is nowhere to study. Raise an Academy and the whole book opens.</div>`
      : `<div class="mg-techhead">The Academy stands at <b>tier ${depth}</b>. ` +
        `It will teach anything up to Age ${Math.min(9, depth * 2)}; deeper Ages want a deeper Academy.</div>`;

  return `${head}<div class="mg-tech-grid">${columns}</div>`;
}
